import {
  type ApolloError,
  NetworkStatus,
  type OperationVariables,
  type TypedDocumentNode,
  useQuery,
} from '@apollo/client';
import { canonicalStringify, getOperationName } from '@apollo/client/utilities';
import { type ReactElement, useState } from 'react';
import { useRouteQueryRead } from './useRouteQueryRead';
import { useRouteLoaderQueryRef, useRouteQueryRefetch } from './useRouteQueryRef';

export type RouteLoaderQuery<TData> = Readonly<{
  data: TData | undefined;
  loading: boolean;
  error: ApolloError | undefined;
  refetch: () => Promise<unknown>;
  /** Render once beside the content: it reads the loader's ref and renders nothing. */
  reader: ReactElement | null;
}>;

/** A failed read of the loader's ref: shown until the page's own query fetches it again. */
type FailedRead = Readonly<{ key: string; error: ApolloError; refetched: boolean }>;

export function useRouteLoaderQuery<TData, TVariables extends OperationVariables>(
  document: TypedDocumentNode<TData, TVariables>,
  variables: TVariables
): RouteLoaderQuery<TData> {
  const loaderRef = useRouteLoaderQueryRef(document, variables);
  const key = canonicalStringify([getOperationName(document), variables]);
  const [held, setHeld] = useState(() => (loaderRef ? { ref: loaderRef, key } : undefined));
  const [live, setLive] = useState(held === undefined);
  const [failed, setFailed] = useState<FailedRead>();
  if (!live && key !== held?.key) setLive(true);
  else if (!live && loaderRef !== undefined && loaderRef !== held?.ref) setHeld({ ref: loaderRef, key });
  const { read, reader } = useRouteQueryRead(live ? undefined : held?.ref);
  if (!live && key === held?.key && read.error !== undefined) {
    setLive(true);
    setFailed({ key, error: read.error, refetched: false });
  }
  if (failed !== undefined && failed.key !== key) setFailed(undefined);
  const resumed = live && failed?.key === key;
  const resumedQuery = useQuery(document, {
    variables,
    skip: !resumed,
    fetchPolicy: 'cache-only',
    notifyOnNetworkStatusChange: true,
  });
  const query = useQuery(document, { variables, skip: !live || resumed, notifyOnNetworkStatusChange: true });
  const refetchInPlace = useRouteQueryRefetch(document, variables);
  if (resumed && !failed.refetched && resumedQuery.networkStatus !== NetworkStatus.ready) {
    setFailed({ ...failed, refetched: true });
  }

  if (resumed) {
    const error = resumedQuery.error ?? (failed.refetched ? undefined : failed.error);
    return { data: resumedQuery.data, loading: resumedQuery.loading, error, refetch: resumedQuery.refetch, reader };
  }
  if (live) return { data: query.data, loading: query.loading, error: query.error, refetch: query.refetch, reader };
  return { data: read.data, loading: !read.settled, error: read.error, refetch: refetchInPlace, reader };
}
